import { fr } from "@codegouvfr/react-dsfr";
import { Badge } from "@codegouvfr/react-dsfr/Badge";
import { Stack } from "@mui/material";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo
} from "react";
import { Notification } from "./Notification";
import { cx } from "@codegouvfr/react-dsfr/fr/cx";
import { useStoreSyncedWithLocalStorage } from "common/store/store";
import { useApi } from "common/utils/api";
import { getNotificationContent } from "./NotificationContent";
import { useSnackbarAlerts } from "../../../common/Snackbar";
import {
  NOTIFICATIONS_QUERY,
  READ_NOTIFICATIONS_MUTATION
} from "common/utils/apiQueries/notifications";

// Matches .fr-accordion__btn's min-height, keeping the collapse's
// paddingBottom and the notifications maxHeight calc in sync.
const TOGGLE_BUTTON_HEIGHT = "3rem";

const mergeNotifications = (apiNotifs, localNotifs) => {
  const localById = Object.fromEntries(localNotifs.map((n) => [n.id, n]));

  const apiIds = new Set(apiNotifs.map((api) => api.id));

  const allNotifs = [
    ...apiNotifs.map((api) =>
      localById[api.id] ? { ...api, read: localById[api.id].read } : api
    ),
    ...localNotifs.filter((local) => !apiIds.has(local.id))
  ];

  return allNotifs.sort(
    (a, b) => new Date(b.creationTime) - new Date(a.creationTime)
  );
};

const InnerNotification = React.memo(
  ({ type, data, read, openHistory, onNotificationClick }) => {
    const details = React.useMemo(() => {
      if (!data) return null;
      return getNotificationContent(type, JSON.parse(data));
    }, [type, data]);

    if (!details) {
      return null;
    }

    const { title, content, missionId, buttonLabel } = details;
    return (
      <Notification
        title={title}
        content={content}
        missionId={missionId}
        historyOnClick={() => onNotificationClick(missionId)}
        read={read}
        buttonLabel={buttonLabel}
      />
    );
  }
);

export const Notifications = ({ openHistory }) => {
  const id = "fr-accordion-notifs";
  const collapseElementId = `${id}-collapse`;

  const store = useStoreSyncedWithLocalStorage();
  const userInfo = store.userInfo();

  const [notifs, setNotifs] = useState(() => userInfo.notifications || []);

  const [isExpended, setIsExpended] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [containerRect, setContainerRect] = useState({
    left: 0,
    width: "100%"
  });
  const api = useApi();
  const alerts = useSnackbarAlerts();

  const isExpendedRef = useRef(isExpended);
  const notifsRef = useRef(notifs);

  useEffect(() => {
    isExpendedRef.current = isExpended;
  }, [isExpended]);

  useEffect(() => {
    const headerEl = document.querySelector(".header-container");
    if (!headerEl) return;

    const updateHeaderHeight = () =>
      setHeaderHeight(headerEl.getBoundingClientRect().height);

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(headerEl);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const containerEl = document.getElementById("content");

    if (!containerEl) return;

    const updateContainerRect = () => {
      const rect = containerEl.getBoundingClientRect();
      setContainerRect({ left: rect.left, width: rect.width });
    };

    updateContainerRect();

    const observer = new ResizeObserver(updateContainerRect);
    observer.observe(containerEl);

    window.addEventListener("resize", updateContainerRect);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateContainerRect);
    }
  }, []);

  useEffect(() => {
    notifsRef.current = notifs;
  }, [notifs]);

  useEffect(() => {
    setNotifs(userInfo.notifications || []);
  }, [userInfo.notifications]);

  useEffect(() => {
    if (!userInfo?.id) return;

    const loadNotifications = async () => {
      await alerts.withApiErrorHandling(async () => {
        const apiResponse = await api.graphQlQuery(
          NOTIFICATIONS_QUERY,
          { id: userInfo.id },
          {}
        );

        const apiNotifs = apiResponse.data.user.notifications;
        const localNotifs = store.userInfo().notifications || [];
        const merged = mergeNotifications(apiNotifs, localNotifs);

        setNotifs(merged);
        await store.setUserInfo({
          ...store.userInfo(),
          notifications: merged
        });
      }, "get-notifications");
    };

    loadNotifications();
  }, [api, store, userInfo?.id, alerts]);

  const updateNotificationsReadStatus = useCallback(
    async (notificationIds, errorKey = "read-notifications") => {
      const updatedNotifs = notifsRef.current.map((n) =>
        notificationIds.includes(n.id) ? { ...n, read: true } : n
      );

      setNotifs(updatedNotifs);
      await store.setUserInfo({
        ...store.userInfo(),
        notifications: updatedNotifs
      });

      // first call to /unexposed may return 403 — tracked in a separate ticket
      try {
        await api.graphQlMutate(
          READ_NOTIFICATIONS_MUTATION,
          { notificationIds },
          { context: { nonPublicApi: true } }
        );
      } catch (err) {
        const is403 = err?.networkError?.statusCode === 403
          || err?.graphQLErrors?.some(e => e?.extensions?.code === 403);
        if (!is403) {
          alerts.error(err?.message || "Erreur", errorKey, 10000);
        }
      }
    },
    [api, store, alerts]
  );

  useEffect(() => {
    return () => {
      if (isExpendedRef.current && notifsRef.current.some((n) => !n.read)) {
        const allNotificationIds = notifsRef.current.map((n) => n.id);
        updateNotificationsReadStatus(allNotificationIds, "read-notifications");
      }
    };
  }, [updateNotificationsReadStatus]);

  const markNotificationAsRead = useCallback(
    async (notificationId) => {
      await updateNotificationsReadStatus(
        [notificationId],
        "read-notification"
      );
    },
    [updateNotificationsReadStatus]
  );

  const markNotificationsAsRead = useCallback(async () => {
    const allNotificationIds = notifsRef.current.map((n) => n.id);
    await updateNotificationsReadStatus(
      allNotificationIds,
      "read-notifications"
    );
  }, [updateNotificationsReadStatus]);

  const handleNotificationClick = useCallback(
    async (missionId) => {
      const notification = notifsRef.current.find((n) => {
        const details = getNotificationContent(n.type, JSON.parse(n.data));
        return details.missionId === missionId;
      });

      if (notification && !notification.read) {
        await markNotificationAsRead(notification.id);
      }

      openHistory(missionId);
    },
    [markNotificationAsRead, openHistory]
  );

  const onExtendButtonClick = useCallback(async () => {
    const isExpended_newValue = !isExpended;
    setIsExpended(isExpended_newValue);

    if (
      isExpended &&
      !isExpended_newValue &&
      notifsRef.current.some((n) => !n.read)
    ) {
      await markNotificationsAsRead();
    }
  }, [isExpended, markNotificationsAsRead]);

  const unreadNotifs = useMemo(() => notifs.filter((n) => !n.read), [notifs]);

  const title = useMemo(
    () => (unreadNotifs.length > 0 ? unreadNotifs.length : ""),
    [unreadNotifs.length]
  );

  const notificationsMaxHeight = headerHeight
    ? `calc(100vh - ${headerHeight}px - ${TOGGLE_BUTTON_HEIGHT})`
    : "65vh";

  return (
    <section
      className={cx(fr.cx("fr-accordion"), "notifications")}
      style={{
        backgroundColor: "white",
        position: "fixed",
        bottom: 0,
        left: containerRect.left,
        width: containerRect.width,
        zIndex: 600
      }}
    >
      <div
        className={fr.cx("fr-collapse")}
        id={collapseElementId}
        style={{ paddingTop: 0, paddingBottom: TOGGLE_BUTTON_HEIGHT }}
      >
        <Stack
          direction="column"
          width="100%"
          maxHeight={notificationsMaxHeight}
          sx={{ overflowY: "auto" }}
        >
          {notifs.length > 0 ? (
            notifs.map((notif) => (
              <InnerNotification
                key={`notif__${notif.id}`}
                {...notif}
                openHistory={openHistory}
                onNotificationClick={handleNotificationClick}
              />
            ))
          ) : (
            <p
              style={{
                margin: 0,
                padding: "1rem 1rem",
                color: fr.colors.decisions.background.flat.grey.default,
                borderBottom: `2px solid ${fr.colors.decisions.border.default.grey.default}`
              }}
            >
              Vous n'avez aucun message
            </p>
          )}
        </Stack>
      </div>
      <h3 className={fr.cx("fr-accordion__title")}>
        <button
          className={fr.cx("fr-accordion__btn")}
          aria-expanded={isExpended}
          aria-controls={collapseElementId}
          onClick={onExtendButtonClick}
          type="button"
          id={`${id}__toggle-btn`}
          style={{
            justifyContent: "flex-start",
            position: "fixed",
            bottom: 0,
            left: containerRect.left,
            width: containerRect.width,
            backgroundColor: "white"
          }}
        >
          <Stack direction="row" gap={1} alignItems="center" pl={1}>
            <span
              className={fr.cx(
                "fr-btn--icon-left",
                "fr-icon-notification-3-line"
              )}
              style={{ fontWeight: 700, fontSize: "1rem" }}
            >
              Informations
            </span>
            {title && (
              <Badge noIcon severity="error" style={{ fontSize: "0.650rem" }}>
                {title}
              </Badge>
            )}
          </Stack>
        </button>
      </h3>
    </section>
  );
};
